import React from 'react';
import { useRecordContext } from 'react-admin';
import { Grid, Box, Divider, Typography, styled } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

import { sectionTitleStyle, smallGrayFontStyle, extraSmallGrayFontStyle, headerFont, rowsFont } from 'src/styles';
import { EducationCenterType, LessonsDurationByLevel } from 'src/components/Employees/types';
import { EducationLevelGroup } from 'src/constants';

const removeSecondsHandler = (time: string | undefined) => {
    if (time) {
        const timeArray = time.split(':');
        timeArray.pop();
        return timeArray.join(':');
    }
    return '-';
};

const ScheduleGrid: React.FC<LessonsDurationByLevel> = ({ lessonDuration, weekdays, educationLevelGroup }) => {
    const createData = (
        name: string,
        firstLesson: string,
        secondLesson: string,
        thirdLesson: string,
        fourthLesson: string,
        fifthLesson: string,
        sixthLesson: string,
        seventhLesson: string,
        eighthLesson: string,
        ninthLesson?: string,
        tenthLesson?: string
    ) => {
        return {
            name,
            firstLesson,
            secondLesson,
            thirdLesson,
            fourthLesson,
            fifthLesson,
            sixthLesson,
            seventhLesson,
            eighthLesson,
            ninthLesson,
            tenthLesson
        };
    };

    const rows = [
        createData(
            'Початок уроку',
            removeSecondsHandler(weekdays.firstLesson?.from),
            removeSecondsHandler(weekdays.secondLesson?.from),
            removeSecondsHandler(weekdays.thirdLesson?.from),
            removeSecondsHandler(weekdays.fourthLesson?.from),
            removeSecondsHandler(weekdays.fifthLesson?.from),
            removeSecondsHandler(weekdays.sixthLesson?.from),
            removeSecondsHandler(weekdays.seventhLesson?.from),
            removeSecondsHandler(weekdays.eighthLesson?.from),
            removeSecondsHandler(weekdays.ninthLesson?.from),
            removeSecondsHandler(weekdays.tenthLesson?.from)
        ),
        createData(
            'Кінець уроку',
            removeSecondsHandler(weekdays.firstLesson?.to),
            removeSecondsHandler(weekdays.secondLesson?.to),
            removeSecondsHandler(weekdays.thirdLesson?.to),
            removeSecondsHandler(weekdays.fourthLesson?.to),
            removeSecondsHandler(weekdays.fifthLesson?.to),
            removeSecondsHandler(weekdays.sixthLesson?.to),
            removeSecondsHandler(weekdays.seventhLesson?.to),
            removeSecondsHandler(weekdays.eighthLesson?.to),
            removeSecondsHandler(weekdays.ninthLesson?.to),
            removeSecondsHandler(weekdays.tenthLesson?.to)
        )
    ];

    const StyledHeaderTableCell = styled(TableCell)({ ...extraSmallGrayFontStyle, ...headerFont });
    const StyledRowTableCell = styled(TableCell)({ ...extraSmallGrayFontStyle, ...rowsFont });

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableRow>
                        <StyledHeaderTableCell>Тривалість уроку - {lessonDuration} хв</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">1-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">2-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">3-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">4-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">5-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">6-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">7-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">8-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">9-ий урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">10-ий урок</StyledHeaderTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <StyledRowTableCell component="th" scope="row">
                                {row.name}
                            </StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.firstLesson} </StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.secondLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.thirdLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.fourthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.fifthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.sixthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.seventhLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.eighthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.ninthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.tenthLesson}</StyledRowTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

/**
 * Formats phone number string +380123456789 to +38 (012) 345-67-89
 * @param { string } phoneNumber
 * @returns { string }
 */
const getFormattedPhoneNumber = (phoneNumber: string): string => {
    const arrayLikePhoneNumber = phoneNumber.split('');

    return arrayLikePhoneNumber
        .map((character: string, i: number): string => {
            switch (i) {
                case 3:
                    return ` (${character}`;
                case 5:
                    return `${character}) `;
                case 9:
                case 11:
                    return `-${character}`;
                default:
                    return character;
            }
        })
        .join('');
};

const GeneralCenterInfoTab: React.FC = () => {
    const record = useRecordContext<EducationCenterType>();
    if (!record) return null;

    return (
        <Grid p={3} container columnSpacing={4} rowSpacing={1}>
            <Grid item xs={12}>
                <Box sx={{ ...sectionTitleStyle, mb: 3 }} whiteSpace="normal">
                    Контактна інформація
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Контактний номер телефону
                </Box>
                <Box mb={0.3}>{getFormattedPhoneNumber(record.phone)}</Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Адреса
                </Box>
                <Box mb={0.3}>{record.address}</Box>
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ ...sectionTitleStyle, mb: 3 }} whiteSpace="normal">
                    Робочі години
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Робочі години
                </Box>
                <Box mb={0.3}>
                    {removeSecondsHandler(record?.workStart)} - {removeSecondsHandler(record?.workEnd)}
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Перерва на обід
                </Box>
                <Box mb={0.3}>
                    {removeSecondsHandler(record?.breakStart)} - {removeSecondsHandler(record?.breakEnd)}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ ...sectionTitleStyle, mb: 3 }} whiteSpace="normal">
                    Тривалість уроків
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        Дошкільна освіта
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={record.lessonsDuration.beforeSchoolEducation.lessonDuration}
                        weekdays={record.lessonsDuration.beforeSchoolEducation.weekdays}
                        educationLevelGroup={EducationLevelGroup.PRESCHOOL}
                    />
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        1-4 класи
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={record.lessonsDuration.firstFourthGrade.lessonDuration}
                        weekdays={record.lessonsDuration.firstFourthGrade.weekdays}
                        educationLevelGroup={EducationLevelGroup.JUNIOR_SCHOOL}
                    />
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        5-11 класи
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={record.lessonsDuration.fifthTenthGrade.lessonDuration}
                        weekdays={record.lessonsDuration.fifthTenthGrade.weekdays}
                        educationLevelGroup={EducationLevelGroup.HIGH_SCHOOL}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};

export default GeneralCenterInfoTab;
